/**
 * 상품연동형 옵션 핸들러
 * 옵션 선택 시 다른 상품과 연동되는 로직
 */
import { BaseOptionHandler } from './BaseOptionHandler';

export class LinkedProductOptionHandler extends BaseOptionHandler {

     /**
     * 옵션 데이터 초기화
     * @param {Array} options - API에서 받은 옵션 데이터
     * @returns {Array} 초기화된 옵션 데이터
     */
     initializeOptions(options) {
        const selectOptions = options.filter(option => (!option.isTextOption && !option.isFileOption && option.required));
        // 상품 연동형 옵션의 경우에만 [선택] 옵션을 textOption 처럼 취급
        const textAndFileOptions = options.filter(option => (option.isTextOption || !option.required));
        
        return {
            initializedSelectOptions: selectOptions.map(option => ({
                ...option,
                selectedIndex: null,
                selectedValue: null,
                value: option.value,
                isFocused: false
            })),
            initializedTextAndFileOptions: textAndFileOptions.map(option => ({
                ...option,
                selectedIndex: null,
                selectedValue: option.isTextOption ? '' : null,
                value: option.isTextOption ? '' : option.value,
                isFocused: false
            })),
        };
    }

    /**
     * 옵션 선택 가능 여부 확인
     * 이전 옵션이 모두 선택되어야 다음 옵션 선택 가능
     */
    canSelectOption(optionIndex, productOptions) {
        // 첫 번째 옵션은 항상 선택 가능
        if (optionIndex === 0) {
            return true;
        }

        // 선택형 옵션은 항상 선택 가능
        if (!productOptions[optionIndex].required) {
            return true;
        }

        // 이전 옵션들이 모두 선택되어 있는지 확인
        for (let i = 0; i < optionIndex; i++) {
            const option = productOptions[i];
            if (option.required) {
                if (option.isTextOption) {
                    if (!option.selectedValue || option.selectedValue.trim() === '') {
                        return false;
                    }
                } else {
                    if (option.selectedIndex === null) {
                        return false;
                    }
                }
            }
        }

        return true;
    }

    infoFetchFailedToast() {
        this.setToastMessage('상품 정보를 불러오는데 실패했어요');
        this.setIsToastVisible(true);
    }

    /**
     * 옵션 선택 시: 순서대로 선택하도록 강제
     */
    async onOptionSelect(props) {
        const { 
            optionIndex,
            selectedIdx,
            productOptions,
            renderableOptions,
            allVariants,
            textAndFileOptions,
            optionSelectedProductList,
            setProductOptions,
            setRenderableOptions,
            setOptionSelectedProductList,
            initialProductOptions,
        } = props;
        // 선택 가능 여부 확인
        if (!this.canSelectOption(optionIndex, productOptions)) {
            return;
        }

        const selectedOption = productOptions[optionIndex];
        const onlyVariant = allVariants[0];     // 상품연동형은 하나의 variant만 존재
        // A. 이번 선택이 required 인지 확인
            // A-false, 독립형처럼 바로 variant 호출 후 상품 추가
        if (!selectedOption.required) {
            const unrequiredCombination = selectedOption?.optionName + ' - ' + selectedOption.value[selectedIdx]?.optionText;
            if (!unrequiredCombination) {
                // combination 생성 실패
                return;
            }
            // 이미 존재하는 combination이면 그냥 종료
            if (optionSelectedProductList.some(product => product.combination === unrequiredCombination)) {
                this.combinationAlreadyExistsToast();
                return;
            }
            // 최대 품목 개수 체크
            if (optionSelectedProductList.length === this.MAX_QUANTITY) {
                this.maxNumberOfProductsToast();
                return;
            }
            // 상품 추가
            setOptionSelectedProductList(prev => [...prev, {
                productName: selectedOption.value[selectedIdx]?.optionText,
                combination: unrequiredCombination,
                noOptionText: true,
                optionValueList: [selectedOption.value[selectedIdx]?.optionText],
                optionCodeList: [selectedOption?.optionCode ?? ''],
                optionValueNoList: [selectedOption.value[selectedIdx]?.valueNo ?? ''],
                textOptions: textAndFileOptions,
                quantity: 1,
                setQuantity: (quantity) => this.handleQuantityChange(unrequiredCombination, quantity),
                price: this.productInfo?.price,
                additionalPrice: selectedOption.value[selectedIdx]?.additionalPrice || 0,
                processProductDeleteClicked: () => this.handleProductDeleteClicked(unrequiredCombination),
                variantId: onlyVariant.variantId,
            }]);
            return;     // A-false로 종료
        }

        // A-true: required 이면 
        // B. 이번 선택이 원래 채워져 있던 값을 바꾸는 것인지 확인
            // B-true, 이후 옵션 초기화 (선택 값 초기화), 이번 옵션 값 업데이트
        if (selectedOption.selectedIndex !== null) {
            // 이 다음 옵션들 초기화, 이번 옵션 값 업데이트
            // 업데이트된 상태를 사용하기 위해 함수형 업데이트 내에서 모든 로직 처리
            setProductOptions(prev => {
                const updatedOptions = prev.map((option, index) => index > optionIndex ? ({
                    ...option,
                    selectedIndex: null,
                    selectedValue: option.isTextOption ? '' : null,
                    isFocused: false,
                    value: this.initialProductOptions[index].value
                }) : index === optionIndex ? ({
                    ...option,
                    selectedIndex: selectedIdx,
                    selectedValue: option.value[selectedIdx]?.optionText || '---'
                }) : option);
                
                return updatedOptions;
            });
            return;     // B-true로 종료
        }
        
        // B-false: 다음 값을 선택한 것이면
        // C. 이번 선택으로 모든 required 옵션이 다 채워지는지 확인
            // C-true, 옵션 값 초기화 및 combination 생성
            // 아직 optionSelectedProductList 에 없는 combination 이면 variant 호출 후 상품 추가
        const requiredOptionsFilledWithCurrentSelection = 
            productOptions.every((option, index) => {
                if (option.required) {
                    return (option.selectedIndex !== null) || (index === optionIndex);
                } else {
                    return true;
                }
            });
        if (requiredOptionsFilledWithCurrentSelection) {
            const requiredProductOptionsWithCurrentSelection = productOptions.map((option, index) => index === optionIndex ? ({
                ...option,
                selectedIndex: selectedIdx,
                selectedValue: option.value[selectedIdx]?.optionText || '---'
            }) : option).filter(option => option.required);
            const requiredCombination = requiredProductOptionsWithCurrentSelection.map(option => option.selectedValue).join('-');
            if (!requiredCombination) {
                // combination 생성 실패
                return;
            }
            // 이미 존재하는 combination이면 그냥 종료
            if (optionSelectedProductList.some(product => product.combination === requiredCombination)) {
                this.combinationAlreadyExistsToast();
                return;
            }
            // 최대 품목 개수 체크
            if (optionSelectedProductList.length === this.MAX_QUANTITY) {
                this.maxNumberOfProductsToast();
                return;
            }
            // 상품 추가
            setOptionSelectedProductList(prev => [...prev, {
                productName: this.productInfo?.name,
                combination: requiredCombination,
                noOptionText: false,
                optionValueList: requiredProductOptionsWithCurrentSelection.map(option => option.selectedValue),
                optionCodeList: requiredProductOptionsWithCurrentSelection.map(option => option?.optionCode ?? ''),
                optionValueNoList: requiredProductOptionsWithCurrentSelection.map(option => option.value[option.selectedIndex]?.valueNo ?? ''),
                textOptions: textAndFileOptions,
                quantity: 1,
                setQuantity: (quantity) => this.handleQuantityChange(requiredCombination, quantity),
                price: this.productInfo?.price,
                additionalPrice: requiredProductOptionsWithCurrentSelection.reduce((acc, option) => acc + (option.value[option.selectedIndex]?.additionalPrice || 0), 0),
                processProductDeleteClicked: () => this.handleProductDeleteClicked(requiredCombination),
                variantId: onlyVariant.variantId,
            }]);
            
            // 전체 옵션 초기화
            setProductOptions(initialProductOptions);
            return;     // C-true로 종료
        }

        // C-false: 아직 required 옵션이 채워지는 중이면
            // productOptions 업데이트
        setProductOptions(prev => {
            const updatedOptions = prev.map((option, index) => index === optionIndex ? ({
                ...option,
                selectedIndex: selectedIdx,
                selectedValue: option.value[selectedIdx]?.optionText || '---'
            }) : option);
            
            return updatedOptions;
        });
        return;     // C-false로 종료
    }

    /**
     * 렌더링할 옵션 리스트 반환
     * 선택 불가능한 옵션은 비활성화 표시
     */
    getRenderableOptions(productOptions, allVariants) {
        return productOptions
            .filter(option => (!option.isTextOption && !option.isFileOption))
            .map((option, index) => ({
                ...option,
                disabled: !this.canSelectOption(index, productOptions)
            }));
    }
}